/* Concur Expense Uploader — vanilla JS + Cognito Hosted UI (PKCE, implicit-ish via code flow) */

// IMPORTANT: cognitoDomain은 Cognito Hosted UI 도메인이어야 한다.
// 절대 `auth.starheart.kr` 로 두지 말 것 — 그 도메인은 starheart-auth admin 웹앱의
// CloudFront(`d22lohs2wp79pn.cloudfront.net`)에 연결된 Route53 alias이지
// Cognito Hosted UI가 아니다. 잘못 지정하면 로그인 시도가 admin 앱으로 리디렉션된다.
// 올바른 값은 `<poolPrefix>.auth.<region>.amazoncognito.com` 포맷이며,
// User Pool us-west-2_MgRKNfkGQ 의 Cognito Domain prefix는 "starheart-auth" 로 고정되어 있다.
// 확인: `aws cognito-idp describe-user-pool-domain --domain starheart-auth --region us-west-2`
const CONFIG = {
  cognitoDomain: "starheart-auth.auth.us-west-2.amazoncognito.com",
  clientId: "63ukp010kl9r4nb0itrajtlf7t",
  redirectUri: window.location.origin + "/callback",
  logoutUri: window.location.origin,
  scope: "openid email profile",
  region: "us-west-2",
};

const $ = (id) => document.getElementById(id);

// ---------- PKCE helpers ----------
function b64url(bytes) {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
async function pkcePair() {
  const random = new Uint8Array(64);
  crypto.getRandomValues(random);
  const verifier = b64url(random);
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-256",
    new TextEncoder().encode(verifier)));
  const challenge = b64url(hash);
  return { verifier, challenge };
}

// ---------- auth state ----------
function saveToken(obj) {
  localStorage.setItem("cf_token", JSON.stringify({ ...obj, saved_at: Date.now() }));
}
function loadToken() {
  try { return JSON.parse(localStorage.getItem("cf_token") || "null"); }
  catch { return null; }
}
function clearToken() { localStorage.removeItem("cf_token"); }

function isTokenValid(t) {
  if (!t || !t.id_token || !t.expires_in) return false;
  return (Date.now() - t.saved_at) < (t.expires_in - 30) * 1000;
}

function parseJwt(token) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payload.length % 4 ? "=".repeat(4 - (payload.length % 4)) : "";
    const bin = atob(payload + pad);
    const bytes = Uint8Array.from(bin, c => c.charCodeAt(0));
    const json = new TextDecoder("utf-8").decode(bytes);
    return JSON.parse(json);
  } catch { return {}; }
}

// ---------- login flow ----------
async function startLogin() {
  const { verifier, challenge } = await pkcePair();
  sessionStorage.setItem("pkce_verifier", verifier);
  const url = new URL(`https://${CONFIG.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set("client_id", CONFIG.clientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", CONFIG.scope);
  url.searchParams.set("redirect_uri", CONFIG.redirectUri);
  url.searchParams.set("identity_provider", "Google");
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", challenge);
  window.location.assign(url.toString());
}

async function handleCallback() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (!code) return;
  const verifier = sessionStorage.getItem("pkce_verifier");
  if (!verifier) { alert("PKCE verifier lost"); return; }
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CONFIG.clientId,
    code,
    redirect_uri: CONFIG.redirectUri,
    code_verifier: verifier,
  });
  const resp = await fetch(`https://${CONFIG.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const data = await resp.json();
  if (data.id_token) {
    saveToken(data);
    sessionStorage.removeItem("pkce_verifier");
    window.history.replaceState({}, "", "/");
  } else {
    alert("Login failed: " + JSON.stringify(data));
  }
}

function logout() {
  clearToken();
  const url = new URL(`https://${CONFIG.cognitoDomain}/logout`);
  url.searchParams.set("client_id", CONFIG.clientId);
  url.searchParams.set("logout_uri", CONFIG.logoutUri);
  window.location.assign(url.toString());
}

// ---------- app ----------
async function api(path, opts = {}) {
  const t = loadToken();
  if (!t) throw new Error("no token");
  const headers = new Headers(opts.headers || {});
  headers.set("Authorization", "Bearer " + t.id_token);
  const resp = await fetch(path, { ...opts, headers });
  if (!resp.ok) {
    const txt = await resp.text();
    throw new Error(`${resp.status}: ${txt}`);
  }
  return resp.json();
}

let _indexCache = [];

async function refreshIndex() {
  const ul = $("index-list");
  ul.innerHTML = "<li class='sub'>loading...</li>";
  try {
    const data = await api("/api/list");
    if (!data.receipts || !data.receipts.length) {
      ul.innerHTML = "<li class='sub'>업로드된 영수증 없음</li>";
      _indexCache = [];
      return;
    }
    const sorted = [...data.receipts].sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
    _indexCache = sorted;
    const visible = sorted.slice(0, 40);
    ul.innerHTML = visible.map((r, i) => {
      const p = r.parsed || {};
      return `<li data-idx="${i}">
        <div class="rcpt-row">
          <input type="checkbox" class="rcpt-check" data-id="${escapeHtml(r.id || "")}">
          <div class="rcpt-body clickable" data-idx="${i}">
            <strong>${escapeHtml(p.date || "?")} ${escapeHtml(p.time || "")}</strong>
            · ${escapeHtml(p.vendor || "?")} · KRW ${p.amount_value ?? "?"}
            <span class="pill ${p.confidence === "high" ? "ok" : ""}">${escapeHtml(p.confidence || "")}</span>
            <br><span class="sub">s3://${escapeHtml(r.s3_bucket)}/${escapeHtml(r.s3_key)}</span>
          </div>
        </div>
      </li>`;
    }).join("");
    ul.querySelectorAll(".rcpt-body.clickable").forEach(el => {
      el.onclick = () => openReceiptModal(_indexCache[parseInt(el.dataset.idx, 10)]);
    });
    ul.querySelectorAll(".rcpt-check").forEach(cb => {
      cb.onchange = updateDeleteButton;
    });
    $("select-all").checked = false;
    updateDeleteButton();
  } catch (e) {
    ul.innerHTML = `<li class='pill err'>${escapeHtml(e.message)}</li>`;
  }
}

async function openReceiptModal(r) {
  if (!r) return;
  const body = $("modal-body");
  const p = r.parsed || {};
  body.innerHTML = `
    <h3 style="margin:0 0 10px;">영수증 상세</h3>
    <div class="sub" style="margin-bottom:8px;">이미지 불러오는 중...</div>
    <div id="modal-img-wrap"></div>
    <dl style="margin-top:14px;">
      <dt>Date</dt><dd>${escapeHtml(p.date || "-")} ${escapeHtml(p.time || "")}</dd>
      <dt>Vendor</dt><dd>${escapeHtml(p.vendor || "-")}</dd>
      <dt>Amount</dt><dd>${p.amount_value ?? "-"} ${escapeHtml(p.currency || "")}</dd>
      <dt>Confidence</dt><dd>${escapeHtml(p.confidence || "-")}</dd>
      <dt>Uploaded</dt><dd>${escapeHtml((r.uploaded_at || "").replace("T", " ").slice(0, 19))}</dd>
      <dt>Original</dt><dd>${escapeHtml(r.original_filename || "-")}</dd>
      <dt>S3 Key</dt><dd><span class="sub">${escapeHtml(r.s3_key || "")}</span></dd>
    </dl>
    <div class="actions">
      <button id="modal-download" class="primary">⬇ 다운로드</button>
    </div>
  `;
  $("modal").classList.add("show");
  $("modal-download").onclick = () => downloadReceipt(r);
  try {
    const data = await api("/api/receipt-url?s3_key=" + encodeURIComponent(r.s3_key));
    const wrap = $("modal-img-wrap");
    if (wrap) {
      wrap.innerHTML = `<img class="full" src="${data.url}" alt="receipt">`;
      body.querySelector(".sub")?.remove();
    }
  } catch (e) {
    const wrap = $("modal-img-wrap");
    if (wrap) wrap.innerHTML = `<span class="pill err">이미지 로드 실패: ${escapeHtml(e.message)}</span>`;
  }
}

async function downloadReceipt(r) {
  try {
    const data = await api(
      "/api/receipt-url?download=1&s3_key=" + encodeURIComponent(r.s3_key)
    );
    const a = document.createElement("a");
    a.href = data.url;
    a.download = (r.s3_key || "receipt.jpg").split("/").pop();
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    alert("다운로드 실패: " + e.message);
  }
}

function closeReceiptModal() {
  $("modal").classList.remove("show");
  $("modal-body").innerHTML = "";
}

function getCheckedIds() {
  return [...document.querySelectorAll(".rcpt-check:checked")]
    .map(cb => cb.dataset.id)
    .filter(Boolean);
}

function updateDeleteButton() {
  const n = getCheckedIds().length;
  const btn = $("delete-selected");
  btn.disabled = n === 0;
  btn.textContent = n === 0 ? "🗑 선택 삭제" : `🗑 선택 삭제 (${n})`;
}

async function deleteSelected() {
  const ids = getCheckedIds();
  if (!ids.length) return;
  if (!confirm(`${ids.length}개 영수증을 삭제(marking) 처리합니다. S3 파일은 보존됩니다.\n계속하시겠어요?`)) return;
  const btn = $("delete-selected");
  btn.disabled = true;
  btn.textContent = "삭제 중...";
  try {
    const resp = await fetch("/api/mark-deleted", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer " + loadToken().id_token,
      },
      body: JSON.stringify({ ids }),
    });
    if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
    await refreshIndex();
  } catch (e) {
    alert("삭제 실패: " + e.message);
    updateDeleteButton();
  }
}

function escapeHtml(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

async function uploadFile(file) {
  const li = document.createElement("li");
  const thumb = URL.createObjectURL(file);
  li.innerHTML = `<img class="thumb" src="${thumb}"><strong>${escapeHtml(file.name)}</strong>
    <span class="pill" data-status>uploading...</span>`;
  $("queue").prepend(li);
  try {
    const form = new FormData();
    form.append("file", file);
    const t = loadToken();
    const resp = await fetch("/api/upload", {
      method: "POST",
      headers: { "Authorization": "Bearer " + t.id_token },
      body: form,
    });
    if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
    const data = await resp.json();
    const p = data.receipt?.parsed || {};
    li.querySelector("[data-status]").outerHTML =
      `<span class="pill ok">✓ ${escapeHtml(p.date || "")} ${escapeHtml(p.time || "")} · ${escapeHtml(p.vendor || "")} · KRW ${p.amount_value ?? ""}</span>`;
    refreshIndex();
  } catch (e) {
    li.querySelector("[data-status]").outerHTML =
      `<span class="pill err">${escapeHtml(e.message)}</span>`;
  }
}

function wireDrop() {
  const drop = $("drop");
  const input = $("file");

  // Block the browser default of opening dropped files in a new tab
  // for anywhere in the window, not just the drop zone.
  ["dragover", "drop"].forEach(ev => {
    window.addEventListener(ev, (e) => {
      if (e.target !== input) e.preventDefault();
    });
  });

  drop.addEventListener("click", (e) => {
    // Avoid re-triggering when click originates from the input element itself
    if (e.target !== input) input.click();
  });
  input.addEventListener("change", (e) => {
    const files = [...e.target.files];
    e.target.value = "";  // allow selecting the same filenames again later
    files.forEach(uploadFile);
  });

  ["dragenter", "dragover"].forEach(ev => drop.addEventListener(ev, (e) => {
    e.preventDefault(); e.stopPropagation(); drop.classList.add("hover");
  }));
  ["dragleave"].forEach(ev => drop.addEventListener(ev, (e) => {
    e.preventDefault(); e.stopPropagation(); drop.classList.remove("hover");
  }));
  drop.addEventListener("drop", (e) => {
    e.preventDefault(); e.stopPropagation();
    drop.classList.remove("hover");
    const files = [...(e.dataTransfer?.files || [])];
    files.forEach(uploadFile);
  });
  $("refresh").onclick = refreshIndex;
}

function renderVersionBadge() {
  const v = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0";
  const t = typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : "unknown";
  $("version-badge").textContent = `v${v}_${t}`;
}

function renderUserBadge(claims) {
  const el = $("user-badge");
  if (!claims) { el.textContent = ""; return; }
  const nickname =
    claims.nickname || claims.name || claims.preferred_username ||
    (claims.email ? claims.email.split("@")[0] : "") || "user";
  el.innerHTML = `${escapeHtml(nickname)} | <a id="badge-logout">Sign out</a>`;
  $("badge-logout").onclick = logout;
}

async function init() {
  renderVersionBadge();
  if (window.location.pathname === "/callback" || window.location.search.includes("code=")) {
    await handleCallback();
  }
  const t = loadToken();
  const valid = isTokenValid(t);
  if (valid) {
    const claims = parseJwt(t.id_token);
    $("app").classList.remove("hidden");
    renderUserBadge(claims);
    wireDrop();
    refreshIndex();
  } else {
    $("auth-gate").classList.remove("hidden");
    $("auth-status").textContent = "로그인 필요";
    $("login").classList.remove("hidden");
    renderUserBadge(null);
  }
  $("login").onclick = startLogin;
  $("modal-close").onclick = closeReceiptModal;
  $("modal").addEventListener("click", (e) => {
    if (e.target === $("modal")) closeReceiptModal();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeReceiptModal();
  });
  $("delete-selected").onclick = deleteSelected;
  $("select-all").onchange = (e) => {
    document.querySelectorAll(".rcpt-check").forEach(cb => { cb.checked = e.target.checked; });
    updateDeleteButton();
  };
}

init();
